/**
 * CharacterLoraHub — the landing grid for the Character tab.
 * Lists every existing character LoRA as a card (cover, status, base, dataset
 * size / live training progress) with a per-card action menu, plus a dashed
 * "+ new character LoRA" card that opens the wizard.
 */
import { useQuery } from '@tanstack/react-query';
import { Plus, MoreHorizontal, Images, Clock } from 'lucide-react';

import { listLoras, type Lora } from '@/services/api/loras.js';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

function StatusBadge({ lora }: { lora: Lora }) {
  switch (lora.status) {
    case 'training':
      return <Badge variant="warning">Training {lora.progress ?? 0}%</Badge>;
    case 'ready':
      return <Badge variant="success">Ready</Badge>;
    case 'failed':
      return <Badge variant="destructive">Failed</Badge>;
    case 'generating':
    case 'curating':
      return <Badge variant="info">Preparing</Badge>;
    default:
      return <Badge variant="secondary">Draft</Badge>;
  }
}

function LoraCard({ lora }: { lora: Lora }) {
  return (
    <Card className="overflow-hidden">
      <div className="flex h-24 items-center justify-center bg-muted text-2xl font-semibold text-muted-foreground">
        {lora.name.slice(0, 2)}
      </div>
      <div className="p-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="truncate text-sm font-medium">
              {lora.name}
              {lora.codename ? <span className="text-muted-foreground"> · {lora.codename}</span> : null}
            </p>
            <p className="truncate text-xs text-muted-foreground">{lora.base}</p>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="-mr-1 h-7 w-7 shrink-0">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem disabled={lora.status !== 'ready'}>Use in a project</DropdownMenuItem>
              <DropdownMenuItem>Open</DropdownMenuItem>
              <DropdownMenuItem disabled={lora.status === 'training'}>Retrain</DropdownMenuItem>
              <DropdownMenuItem disabled={lora.status !== 'ready'}>Download .safetensors</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-destructive focus:text-destructive">Delete</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="mt-2">
          <StatusBadge lora={lora} />
        </div>

        {lora.status === 'training' ? (
          <div className="mt-2.5">
            <div className="h-1.5 overflow-hidden rounded-full bg-muted">
              <div className="h-full rounded-full bg-indigo-500" style={{ width: `${lora.progress ?? 0}%` }} />
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              step {lora.step}/{lora.totalSteps} · {lora.eta} left
            </p>
          </div>
        ) : (
          <div className="mt-2.5 flex items-center gap-3 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <Images className="h-3 w-3" /> {lora.images} imgs
            </span>
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" /> {lora.updatedAt}
            </span>
          </div>
        )}
      </div>
    </Card>
  );
}

export function CharacterLoraHub({ onCreate }: { onCreate: () => void }) {
  const { data, isLoading } = useQuery({
    queryKey: ['loras', 'character'],
    queryFn: () => listLoras('character'),
  });

  return (
    <div>
      <p className="mb-3 text-sm text-muted-foreground">{data?.length ?? 0} character LoRAs</p>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        <button
          type="button"
          onClick={onCreate}
          className="flex min-h-[188px] flex-col items-center justify-center gap-2 rounded-lg border border-dashed text-muted-foreground transition-colors hover:border-indigo-500/60 hover:text-foreground"
        >
          <Plus className="h-6 w-6" aria-hidden />
          <span className="text-sm font-medium">New character LoRA</span>
        </button>

        {isLoading
          ? [0, 1, 2].map((i) => <Skeleton key={i} className="h-[188px] rounded-lg" />)
          : data?.map((lora) => <LoraCard key={lora.id} lora={lora} />)}
      </div>
    </div>
  );
}
